/**
 * Coordinate Conversion Utilities for PDF.js
 *
 * Converts between:
 * - Screen coordinates (mouse position in viewport)
 * - PDF coordinates (points on PDF page)
 */

import { PDFPageInfo } from '../types';

/**
 * Convert screen coordinates (from drag event) to PDF coordinates
 *
 * @param screenX - X position in screen pixels (from event.clientX)
 * @param screenY - Y position in screen pixels (from event.clientY)
 * @param pageNumber - Target page number (1-indexed)
 * @param pagesInfo - Array of page info with dimensions and scale
 * @returns PDF coordinates {x, y} in points, or null if page not found
 */
export const convertScreenToPdfCoords = (
  screenX: number,
  screenY: number,
  pageNumber: number,
  pagesInfo: PDFPageInfo[]
): { x: number; y: number } | null => {
  const pageInfo = pagesInfo.find(p => p.pageNumber === pageNumber);
  if (!pageInfo) return null;

  // Get canvas element directly (it has the ID)
  const canvas = document.getElementById(`pdf-page-${pageNumber}`) as HTMLCanvasElement | null;
  if (!canvas) return null;

  // Get canvas bounding rect in screen coordinates
  const rect = canvas.getBoundingClientRect();

  // Calculate position relative to canvas
  const relativeX = screenX - rect.left;
  const relativeY = screenY - rect.top;

  // Convert from screen pixels to PDF points
  // Canvas is rendered at pageInfo.scale, so divide by scale to get PDF points
  const pdfX = relativeX / pageInfo.scale;
  const pdfY = relativeY / pageInfo.scale;

  return {
    x: Math.max(0, Math.min(pdfX, pageInfo.width / pageInfo.scale)),
    y: Math.max(0, Math.min(pdfY, pageInfo.height / pageInfo.scale)),
  };
};

/**
 * Get the page number where a drop event occurred
 *
 * @param screenX - X position in screen pixels
 * @param screenY - Y position in screen pixels
 * @param pagesInfo - Array of page info
 * @returns Page number (1-indexed) or null if not over any page
 */
export const getPageNumberFromScreenCoords = (
  screenX: number,
  screenY: number,
  pagesInfo: PDFPageInfo[]
): number | null => {
  // Check each page to see if drop point is within its bounds
  for (const pageInfo of pagesInfo) {
    const canvas = document.getElementById(`pdf-page-${pageInfo.pageNumber}`) as HTMLCanvasElement | null;
    if (!canvas) continue;

    const rect = canvas.getBoundingClientRect();

    if (
      screenX >= rect.left &&
      screenX <= rect.right &&
      screenY >= rect.top &&
      screenY <= rect.bottom
    ) {
      return pageInfo.pageNumber;
    }
  }

  return null;
};

/**
 * Get default field dimensions by type (in PDF points)
 */
export const getDefaultFieldDimensions = (
  type: 'signature' | 'initials' | 'date' | 'text'
): { width: number; height: number } => {
  switch (type) {
    case 'signature':
      return { width: 150, height: 50 };
    case 'initials':
      return { width: 80, height: 40 };
    case 'date':
      return { width: 120, height: 30 };
    case 'text':
      return { width: 150, height: 30 };
    default:
      return { width: 100, height: 30 };
  }
};

/**
 * Signer color palette - Propdocs brand colors
 * Colors are assigned by signer order (1st signer, 2nd signer, etc.)
 */
const SIGNER_COLORS = [
  '#006C90', // Signer 1 - Primary brand (ocean blue)
  '#7FA8B6', // Signer 2 - Dusty blue
  '#9B96C4', // Signer 3 - Lavender purple
  '#9FB6A0', // Signer 4 - Sage green
  '#D1A29A', // Signer 5 - Dusty rose
  '#C596A5', // Signer 6 - Mauve pink
  '#CDBB9E', // Signer 7 - Warm beige
  '#B2ADA3', // Signer 8 - Warm gray
  '#8F9FA6', // Signer 9 - Cool gray-blue
  '#A8929E', // Signer 10 - Dusty mauve
];

/**
 * Get consistent color for a signer based on their order
 * @param signerOrder - Zero-based order of signer (0 = first signer, 1 = second, etc.)
 * @returns Hex color code from brand palette
 */
export const getSignerColor = (signerOrder: number): string => {
  return SIGNER_COLORS[signerOrder % SIGNER_COLORS.length];
};

/**
 * Get field color by type
 * @deprecated Use getSignerColor instead for signer-based coloring
 */
export const getFieldColor = (type: 'signature' | 'initials' | 'date' | 'text'): string => {
  switch (type) {
    case 'signature':
      return '#4A90E2';
    case 'initials':
      return '#7B68EE';
    case 'date':
      return '#50C878';
    case 'text':
      return '#FFA500';
    default:
      return '#999999';
  }
};
